import {
  ICreatePassportDto,
  IEquipment,
  IEquipmentPassport,
  IEquipmentPassportValue,
} from '@/pages/Equipment/equipment.types.ts'
import SidePage from '@/shared/SidePage'
import SeparateLine from '@/shared/Forms/SeparateLine'
import FormField from '@/shared/Forms/FormField'
import PassportSchemaDropdown from '@/pages/MDM/components/Passports/PassportSchemaDropdown'
import useStateWithChange from '@/shared/hooks/useStateWithChange.ts'
import { IPassportSchema, IPassportSchemaField } from '@/pages/MDM/mdm.types.ts'
import usePassportSchemas from '@/pages/MDM/hooks/usePassportSchemas.ts'
import usePassportSchema from '@/pages/MDM/hooks/usePassprotSchema.ts'
import { useMemo } from 'react'
import objectUtils from '@/utils/object.utils.ts'
import useNestedArrayChangeCallbacks from '@/shared/hooks/useNestedArrayChangeCallbacks.ts'
import Button from '@/shared/Buttons/Button'
import useHttpLoader from '@/shared/hooks/useHttpLoader.ts'
import equipmentApi from '@/pages/Equipment/equipment.api.ts'
import equipmentEvents from '@/pages/Equipment/events/equipment/equipment.events.ts'
import MDMValueInput from '@/pages/MDM/components/MDMValueInput'

interface IProps {
  equipment: IEquipment
  open: boolean
  onClose: () => void
  onCreate: (passport: IEquipmentPassport) => void
}

const CreateEquipmentPassportSidePage = (props: IProps) => {
  const { wait, loading } = useHttpLoader()
  const passportSchemas = usePassportSchemas()
  const [state, setState] = useStateWithChange<ICreatePassportDto>({ passportSchemaId: null, values: [] })
  const passportSchema = usePassportSchema(state.passportSchemaId)

  const { handleElementUpdate: handleValueUpdate } = useNestedArrayChangeCallbacks({
    sourceObjectSetter: setState,
    nestedArrayKey: 'values',
  })

  const handleSchemaSelected = (passportSchemaId: IPassportSchema['id']) => {
    const passport = passportSchemas.find((s) => s.id === passportSchemaId)

    setState((prev) => ({
      ...prev,
      passportSchemaId,
      values: passport.fields.map((f) => ({ fieldId: f.id, value: '', enumValueId: null, unitValueId: null })),
    }))
  }

  const groupedFields: Array<{ id: number; groupName: string; children: IPassportSchemaField[] }> = useMemo(() => {
    if (!passportSchema) return []

    const fieldsByGroup = objectUtils.arrToKeyArray(passportSchema.fields, 'groupId')
    const result: Array<{ id: number; groupName: string; children: IPassportSchemaField[] }> = []

    for (const group of passportSchema.groups) {
      if (fieldsByGroup[group.id]?.length) {
        result.push({
          id: group.id,
          groupName: group.name,
          children: [...fieldsByGroup[group.id]].sort((a, b) => a.orderingIndex - b.orderingIndex),
        })
      }
    }

    return result
  }, [passportSchema])

  const handleValueChange = (field: IPassportSchemaField, val: any, name: string) => {
    const index = state.values.findIndex((v) => v.fieldId === field.id)
    if (index === -1) return

    handleValueUpdate(index, val, name)
  }

  const handleSave = () => {
    wait(equipmentApi.putEquipmentPassport(props.equipment.id, state), (resp) => {
      if (resp.status === 'success') {
        equipmentEvents.updated({ ...props.equipment, passportInstanceId: resp.body.id })
        props.onCreate(resp.body)
      }
    })
  }

  const renderFieldInput = (field: IPassportSchemaField) => {
    const val = state.values.find((v) => v.fieldId === field.id)

    return (
      <MDMValueInput
        value={getPassportFieldValue(field, val)?.toString()}
        schema={field}
        name="value"
        placeHolder="Введите значение"
        onChange={(val, name) => handleValueChange(field, val, name)}
        onUnitValueChange={(val) => handleValueChange(field, val, 'unitValueId')}
        onEnumValueChange={(val) => handleValueChange(field, val, 'enumValueId')}
      />
    )
  }

  const renderFields = () => {
    if (!groupedFields?.length) return null

    return groupedFields.map((f) => (
      <div className="flex-lines gap16" id={`group_${f.id}`}>
        <h5 className="text-heading-5">{f.groupName}</h5>
        {f.children.map((c) => (
          <FormField label={c.name} key={c.id}>
            {renderFieldInput(c)}
          </FormField>
        ))}
      </div>
    ))
  }

  return (
    <SidePage onClose={props.onClose} open={props.open}>
      <div className="flex-lines height100 gap16">
        <h4 className="text-heading-4">Заполнение паспорта оборудования</h4>
        <SeparateLine />
        <FormField label="Схема паспорта" required={true}>
          <PassportSchemaDropdown
            value={state.passportSchemaId}
            name="passportSchemaId"
            onChange={handleSchemaSelected}
          />
        </FormField>
        <SeparateLine />
        {renderFields()}
        <div style={{ marginTop: 'auto' }}>
          <SeparateLine />
        </div>
        <div className="inline-flex-gap gap8" style={{ paddingBottom: '24px' }}>
          <Button size="default" type="primary" onClick={handleSave} loading={loading} text="Создать" />
          <Button size="default" type="default" onClick={props.onClose} disabled={loading} text="Отмена" />
        </div>
      </div>
    </SidePage>
  )
}

const getPassportFieldValue = (schema: IPassportSchemaField, value: Omit<IEquipmentPassportValue, 'id'>) => {
  if (!value) return null

  switch (schema.type) {
    case 'ENUM':
      return value.enumValueId
    case 'UNIT':
      return value.unitValueId
    default:
      return value.value
  }
}

export default CreateEquipmentPassportSidePage
